import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Bot, BarChart3, Settings, LogOut, User, Menu, X } from 'lucide-react';
import { Agent, DashboardFilters, DashboardPagination, SortBy } from '@/types';
import { getAgents, logout } from '@/lib/api';
import { AgentCard } from '@/components/dashboard/AgentCard';
import { CreateAgentForm } from '@/components/agent/CreateAgentForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { cn, debounce } from '@/lib/utils';

export default function Dashboard() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Filters
  const [filters, setFilters] = useState<DashboardFilters>({
    status: 'all',
    search: '',
  });

  // Pagination
  const [pagination, setPagination] = useState<DashboardPagination>({
    page: 1,
    pageSize: 12,
    total: 0,
    hasMore: false,
  });

  // Sorting
  const [sortBy, setSortBy] = useState<SortBy>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch agents
  const fetchAgents = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAgents({
        page: pagination.page,
        pageSize: pagination.pageSize,
        status: filters.status,
        search: filters.search || undefined,
        sortBy,
        sortOrder,
      });
      setAgents(data.agents);
      setPagination({
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
        hasMore: data.hasMore,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [filters.status, sortBy, sortOrder, pagination.page]);

  // Debounced search
  const debouncedSearch = debounce((value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, 300);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const handleStatusFilter = (status: DashboardFilters['status']) => {
    setFilters(prev => ({ ...prev, status }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const toggleSort = () => {
    if (sortBy === 'updatedAt') {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy('updatedAt');
      setSortOrder('desc');
    }
  };

  // Navigation handlers
  const handleAnalyticsClick = () => {
    // TODO: Navigate to global analytics page when implemented
    alert('全域分析頁面開發中...\n\n目前可在 Agent 詳情頁面查看各別 Agent 的分析數據。');
  };

  const handleSettingsClick = () => {
    // TODO: Navigate to user settings page when implemented
    alert('用戶設定頁面開發中...\n\n此頁面將包含個人資料修改、密碼變更等功能。');
  };

  const handleLogout = async () => {
    try {
      logout();
      // Close mobile menu if open
      setMobileMenuOpen(false);
      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 glass border-b border-border/50 z-50">
        <div className="flex items-center justify-between h-full px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/dashboard')}
            >
              <div className="relative">
                <Bot className="h-7 w-7 text-primary" />
                <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-status-running rounded-full animate-pulse" />
              </div>
              <span className="font-display font-bold text-lg hidden sm:block">
                FlexyClaw
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="搜尋 Agent..."
                className="w-64 pl-10 h-9 bg-muted/50 border-border/50"
                onChange={handleSearchChange}
              />
            </div>

            {/* Theme Toggle */}
            <ThemeToggle />

            <Button variant="ghost" size="icon" className="relative">
              <User className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside
          className={cn(
            "fixed lg:static inset-y-0 left-0 z-40 w-64 glass border-r border-border/50 transition-transform duration-300 lg:translate-x-0",
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <nav className="flex flex-col h-full p-4 gap-2">
            <NavItem icon={Bot} label="我的 Agents" active onClick={() => navigate('/dashboard')} />
            <NavItem icon={BarChart3} label="分析" onClick={() => handleAnalyticsClick()} />
            <NavItem icon={Settings} label="設定" onClick={() => handleSettingsClick()} />

            <div className="flex-1" />

            <NavItem icon={LogOut} label="登出" variant="destructive" onClick={() => handleLogout()} />
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-30 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-[calc(100vh-4rem)] p-4 lg:p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 animate-fade-in-down">
              <div>
                <h1 className="font-display font-bold text-2xl lg:text-3xl">
                  我的 <span className="gradient-text">Agents</span>
                </h1>
                <p className="text-muted-foreground mt-1">
                  管理您的 AI 代理
                </p>
              </div>

              <Button
                className="shadow-lg shadow-primary/25"
                onClick={() => setCreateModalOpen(true)}
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">建立新 Agent</span>
                <span className="sm:hidden">建立</span>
              </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6 animate-fade-in-up stagger-1">
              <div className="flex gap-2">
                <FilterButton
                  active={filters.status === 'all'}
                  onClick={() => handleStatusFilter('all')}
                >
                  全部
                </FilterButton>
                <FilterButton
                  active={filters.status === 'running'}
                  variant="status-running"
                  onClick={() => handleStatusFilter('running')}
                >
                  運行中
                </FilterButton>
                <FilterButton
                  active={filters.status === 'paused'}
                  variant="status-paused"
                  onClick={() => handleStatusFilter('paused')}
                >
                  暫停
                </FilterButton>
                <FilterButton
                  active={filters.status === 'draft'}
                  variant="status-draft"
                  onClick={() => handleStatusFilter('draft')}
                >
                  草稿
                </FilterButton>
              </div>

              <div className="sm:ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSort}
                  className="gap-2"
                >
                  排序: {sortBy === 'updatedAt' ? '最近更新' : sortBy === 'createdAt' ? '建立時間' : '名稱'}
                  <span className="text-xs">{sortOrder === 'desc' ? '↓' : '↑'}</span>
                </Button>
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <LoadingState />
            ) : error ? (
              <ErrorState error={error} onRetry={fetchAgents} />
            ) : agents.length === 0 ? (
              <EmptyState onCreate={() => setCreateModalOpen(true)} />
            ) : (
              <>
                {/* Agent Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {agents.map((agent) => (
                    <AgentCard key={agent.agentId} agent={agent} />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.total > pagination.pageSize && (
                  <div className="flex justify-center mt-8">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page === 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      >
                        上一頁
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!pagination.hasMore}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      >
                        下一頁
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Create Agent Modal */}
      <CreateAgentForm
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={fetchAgents}
      />
    </div>
  );
}

// Sidebar Nav Item
function NavItem({
  icon: Icon,
  label,
  active = false,
  variant = 'default',
  onClick
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
  variant?: 'default' | 'destructive';
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
        "hover:bg-accent/50 hover:text-accent-foreground",
        active && "bg-primary/10 text-primary",
        variant === 'destructive' && "text-destructive hover:bg-destructive/10"
      )}
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
    </button>
  );
}

// Filter Button
function FilterButton({
  active,
  variant = 'default',
  children,
  onClick
}: {
  active?: boolean;
  variant?: 'default' | 'status-running' | 'status-paused' | 'status-draft';
  children: React.ReactNode;
  onClick: () => void;
}) {
  const variantClasses = {
    default: active ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    'status-running': active ? 'bg-status-running text-white' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    'status-paused': active ? 'bg-status-paused text-white' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    'status-draft': active ? 'bg-status-draft text-white' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
        variantClasses[variant]
      )}
    >
      {children}
    </button>
  );
}

// Loading State
function LoadingState() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className={cn(
            "h-48 rounded-xl border border-border bg-card/50 animate-pulse",
            "stagger-" + ((i % 5) + 1)
          )}
        />
      ))}
    </div>
  );
}

// Error State
function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 animate-fade-in">
      <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <span className="text-3xl">⚠️</span>
      </div>
      <h3 className="font-display font-semibold text-lg mb-2">載入失敗</h3>
      <p className="text-muted-foreground mb-6">{error}</p>
      <Button onClick={onRetry} variant="outline">
        <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        重新載入
      </Button>
    </div>
  );
}

// Empty State
function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 animate-fade-in-up">
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-gradient-mesh blur-3xl opacity-30" />
        <div className="relative">
          <Bot className="h-20 w-20 text-muted-foreground/50" />
        </div>
      </div>

      <h3 className="font-display font-semibold text-xl mb-2">
        還沒有任何 <span className="gradient-text">Agent</span>
      </h3>
      <p className="text-muted-foreground mb-8 text-center max-w-md">
        建立您的第一個 AI Agent，開始服務您的用戶
      </p>

      <div className="bg-card/50 border border-border/50 rounded-xl p-6 mb-8 max-w-md">
        <h4 className="font-medium mb-4">建立流程：</h4>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-display font-bold text-primary">
              1
            </div>
            <span className="text-muted-foreground">填寫基本資訊</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-display font-bold text-primary">
              2
            </div>
            <span className="text-muted-foreground">配置 LINE 整合</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-display font-bold text-primary">
              3
            </div>
            <span className="text-muted-foreground">設定 Prompt</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-display font-bold text-primary">
              4
            </div>
            <span className="text-muted-foreground">完成設定</span>
          </div>
        </div>
      </div>

      <Button size="lg" className="shadow-lg shadow-primary/25" onClick={onCreate}>
        <Plus className="h-5 w-5 mr-2" />
        建立第一個 Agent
      </Button>
    </div>
  );
}
